"use client";

// src/components/organisms/legal/google-analytics-consent.tsx
// Same gate as <HubspotTracking>, for gtag.js: GA runs unless the visitor has
// rejected the "analytics" cookie category (opt-out — see IMPLIED_CONSENT in
// lib/consent.ts). Mounted in the root layout, so a rejection made on a
// marketing page also silences GA on app surfaces, which never show the banner.

import { useEffect, useRef, useState } from "react";
import { GoogleAnalytics } from "@next/third-parties/google";
import { siteDetails } from "@/data/siteDetails";
import {
  type AnalyticsConsent,
  CONSENT_CHANGED_EVENT,
  consentAction,
  readAnalyticsConsent,
} from "@/lib/consent";
import { resumeGoogleAnalytics, stopGoogleAnalytics } from "@/lib/google-analytics";

export function GoogleAnalyticsConsent() {
  const [analytics, setAnalytics] = useState<AnalyticsConsent>(null);
  const [injected, setInjected] = useState(false);
  // gtag.js cannot be unloaded, so the tag stays mounted once injected and
  // withdrawal flips `ga-disable-<id>` instead.
  const startedRef = useRef(false);

  useEffect(() => {
    function sync() {
      setAnalytics(readAnalyticsConsent());
    }
    sync();
    window.addEventListener(CONSENT_CHANGED_EVENT, sync);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, sync);
  }, []);

  useEffect(() => {
    switch (consentAction(analytics, startedRef.current)) {
      // Not moved to a render-time adjustment (the usual
      // react-hooks/set-state-in-effect fix): consentAction is a pure
      // function of (analytics, startedRef.current), so setting
      // startedRef.current here changes what the *next* evaluation of this
      // same switch would return. Flipping it during render, before this
      // Effect runs, would make consentAction see started=true on this same
      // pass and return "resume" instead of "load" — firing
      // resumeGoogleAnalytics() immediately after the tag is first injected,
      // before gtag has actually loaded.
      case "load":
        startedRef.current = true;
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setInjected(true);
        break;
      case "resume":
        resumeGoogleAnalytics();
        break;
      case "stop":
        stopGoogleAnalytics();
        break;
    }
  }, [analytics]);

  if (!injected) return null;

  return <GoogleAnalytics gaId={siteDetails.googleAnalyticsId} />;
}
